import Button from '@pinecast/common/Button';
import TextArea from '@pinecast/common/TextArea';
import {FormattedMessage} from '@pinecast/i18n';
import {useCSS} from '@pinecast/styles';
import * as React from 'react';

type Props = {
  index: number;
  text: string;
  onEdit?: (text: string, index: number) => Promise<void>;
  onSplit?: () => Promise<void>;
};

const OnBlurTextArea = ({
  value,
  onChange,
  onSplit,
}: {
  value: string;
  onChange: (value: string) => Promise<void>;
  onSplit?: () => Promise<void>;
}) => {
  const [text, setText] = React.useState(value);
  const [pending, setPending] = React.useState(false);
  const [splitPending, setSplitPending] = React.useState(false);

  const submit = React.useCallback(() => {
    setPending(true);
    onChange(text).finally(() => setPending(false));
  }, [text, onChange]);

  const handleSplit = React.useCallback(() => {
    if (!onSplit) return;
    setSplitPending(true);
    onSplit().finally(() => setSplitPending(false));
  }, [onSplit]);

  // Sync local state when value prop changes (after save completes)
  React.useEffect(() => {
    setText(value);
  }, [value]);

  // Check if text can be split (contains double newlines)
  const canSplit = onSplit && /\n\n+/.test(value);
  const hasChanges = text !== value;

  return (
    <div>
      <TextArea
        rows={3}
        value={text}
        onChange={setText}
        nativeEvents={{onBlur: submit}}
      />
      {hasChanges && (
        <Button $isPrimary size="small" onClick={submit} pending={pending}>
          <FormattedMessage
            description="Label for the save button"
            defaultMessage="Save"
          />
        </Button>
      )}
      {canSplit && !hasChanges && (
        <Button size="small" onClick={handleSplit} pending={splitPending}>
          <FormattedMessage
            description="Label for the split button to split an utterance at paragraph breaks"
            defaultMessage="Split"
          />
        </Button>
      )}
    </div>
  );
};

const TranscriptUtteranceEditor = ({text, index, onEdit, onSplit}: Props) => {
  const css = useCSS();
  const handleChange = React.useCallback(
    (value: string) => {
      if (value !== text && onEdit) {
        return onEdit(value, index);
      }
      return Promise.resolve();
    },
    [text, index, onEdit],
  );
  return onEdit ? (
    <OnBlurTextArea value={text} onChange={handleChange} onSplit={onSplit} />
  ) : (
    <p className={css({margin: 0})}>{addLineBreaks(text)}</p>
  );
};

export default TranscriptUtteranceEditor;

function addLineBreaks(text: string) {
  // Replace three or more line breaks in a row with two line breaks
  const normalized = text.replace(/\n{3,}/g, '\n\n');
  return normalized.split('\n').map((chunk, i) => (
    <React.Fragment key={i}>
      {Boolean(i) && <br />}
      {chunk}
    </React.Fragment>
  ));
}
